import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { createTemplate, deleteTemplate, listTemplates } from './api';
export const TaskTemplates = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    // Add form
    const [addName, setAddName] = useState('');
    const [addInstructions, setAddInstructions] = useState('');
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState(null);
    // Delete
    const [deletingId, setDeletingId] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const load = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const result = await listTemplates();
            setTemplates(result);
        }
        catch (e) {
            setLoadError(e.message ?? 'Failed to load templates');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);
    const handleAdd = async (e) => {
        e.preventDefault();
        if (!addName.trim()) {
            setAddError('Template name is required.');
            return;
        }
        setAdding(true);
        setAddError(null);
        try {
            const tmpl = await createTemplate({
                name: addName.trim(),
                instructions: addInstructions.trim() || undefined,
            });
            setTemplates((prev) => [...prev, tmpl]);
            setAddName('');
            setAddInstructions('');
        }
        catch (e) {
            setAddError(e.message ?? 'Failed to create template');
        }
        finally {
            setAdding(false);
        }
    };
    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete template "${name}"? This cannot be undone.`))
            return;
        setDeletingId(id);
        setDeleteError(null);
        try {
            await deleteTemplate(id);
            setTemplates((prev) => prev.filter((t) => t.id !== id));
        }
        catch (e) {
            setDeleteError(e.message ?? 'Failed to delete template');
        }
        finally {
            setDeletingId(null);
        }
    };
    const containerStyle = {
        maxWidth: isMobile ? '100%' : '640px',
        margin: '0 auto',
    };
    return (_jsx(Layout, { children: _jsxs("div", { style: containerStyle, children: [_jsxs("div", { style: styles.pageHeader, children: [_jsx("button", { style: styles.backButton, onClick: () => navigate('/tasks'), children: "\u2190 Back to Tasks" }), _jsx("h2", { style: styles.heading, children: "Task Templates" }), _jsx("p", { style: styles.subheading, children: "Reusable task definitions to speed up task creation" })] }), loadError && _jsx("div", { style: styles.errorBanner, children: loadError }), deleteError && _jsx("div", { style: styles.errorBanner, children: deleteError }), _jsxs("div", { style: styles.card, children: [_jsx("h3", { style: styles.sectionTitle, children: "Existing Templates" }), loading ? (_jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) })) : templates.length === 0 ? (_jsx("p", { style: styles.emptyText, children: "No templates yet. Add one below." })) : (_jsx("div", { style: styles.templateList, children: templates.map((tmpl, idx) => (_jsxs("div", { style: {
                                    ...styles.templateItem,
                                    borderBottom: idx < templates.length - 1 ? '1px solid rgba(0,0,0,0.07)' : 'none',
                                }, children: [_jsxs("div", { style: styles.templateInfo, children: [_jsx("span", { style: styles.templateName, children: tmpl.name }), tmpl.instructions && (_jsx("p", { style: styles.templateInstructions, children: tmpl.instructions.length > 120
                                                    ? tmpl.instructions.slice(0, 120) + '…'
                                                    : tmpl.instructions }))] }), _jsx("button", { style: {
                                            ...styles.deleteButton,
                                            opacity: deletingId === tmpl.id ? 0.5 : 1,
                                        }, onClick: () => handleDelete(tmpl.id, tmpl.name), disabled: deletingId === tmpl.id, children: deletingId === tmpl.id ? '…' : 'Delete' })] }, tmpl.id))) }))] }), _jsxs("div", { style: { ...styles.card, marginTop: '16px' }, children: [_jsx("h3", { style: styles.sectionTitle, children: "Add Template" }), _jsxs("form", { onSubmit: handleAdd, children: [_jsxs("div", { style: styles.field, children: [_jsxs("label", { style: styles.label, children: ["Name ", _jsx("span", { style: styles.required, children: "*" })] }), _jsx("input", { type: "text", style: styles.input, value: addName, onChange: (e) => setAddName(e.target.value), placeholder: "Template name", disabled: adding, required: true })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Instructions" }), _jsx("textarea", { style: styles.textarea, value: addInstructions, onChange: (e) => setAddInstructions(e.target.value), placeholder: "Default instructions for this template (optional)", disabled: adding, rows: 4 })] }), addError && _jsx("div", { style: styles.errorBanner, children: addError }), _jsx("div", { style: { display: 'flex', justifyContent: 'flex-end' }, children: _jsx("button", { type: "submit", style: { ...styles.addButton, opacity: adding ? 0.6 : 1 }, disabled: adding, children: adding ? 'Adding…' : 'Add Template' }) })] })] })] }) }));
};
const styles = {
    pageHeader: {
        marginBottom: '24px',
    },
    backButton: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 8px 0',
        display: 'block',
        minHeight: '44px',
    },
    heading: {
        fontSize: '34px',
        fontWeight: '700',
        margin: '0 0 6px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '15px',
        color: '#86868b',
        margin: 0,
    },
    card: {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        padding: '24px 28px',
    },
    sectionTitle: {
        fontSize: '17px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 16px 0',
        letterSpacing: '-0.2px',
    },
    templateList: {
        display: 'flex',
        flexDirection: 'column',
    },
    templateItem: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '14px 0',
    },
    templateInfo: {
        flex: 1,
    },
    templateName: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1d1d1f',
    },
    templateInstructions: {
        fontSize: '13px',
        color: '#86868b',
        margin: '4px 0 0 0',
        lineHeight: '1.4',
    },
    deleteButton: {
        padding: '7px 16px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        color: '#c0392b',
        border: '1px solid rgba(255,59,48,0.25)',
        borderRadius: '980px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        minHeight: '36px',
        flexShrink: 0,
    },
    emptyText: {
        fontSize: '14px',
        color: '#86868b',
        textAlign: 'center',
        padding: '20px 0',
        margin: 0,
    },
    field: {
        marginBottom: '16px',
    },
    label: {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#3c3c43',
        marginBottom: '6px',
    },
    required: {
        color: '#c0392b',
    },
    input: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
    },
    textarea: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        minHeight: '100px',
    },
    errorBanner: {
        padding: '12px 16px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '10px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '12px',
    },
    addButton: {
        padding: '12px 24px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '40px 0',
    },
    spinner: {
        width: '28px',
        height: '28px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
};
//# sourceMappingURL=TaskTemplates.js.map